import { useCallback, useEffect, useRef, useState } from 'react';

export type SubmitRevealTransitionPhase =
  | 'question'
  | 'submitting'
  | 'results-entering'
  | 'results';

interface UseSubmitRevealTransitionOptions {
  prefersReducedMotion: boolean;
  onInteractionComplete?: () => void;
  submitTransitionMs: number;
  resultsEnterDelayMs: number;
  /**
   * Starting phase (default 'question'). NA-319: a block rehydrated as
   * already-answered on mount passes 'results' here so it renders its
   * settled results view immediately, with no submit animation and without
   * firing `onInteractionComplete` (mirrors `revealImmediately`).
   */
  initialPhase?: SubmitRevealTransitionPhase;
}

interface UseSubmitRevealTransitionResult {
  transitionPhase: SubmitRevealTransitionPhase;
  isQuestionStageVisible: boolean;
  isResultsStageVisible: boolean;
  isSubmitting: boolean;
  startTransition: () => void;
  resetTransition: () => void;
  /**
   * Jumps straight to the settled 'results' phase with no animation and
   * without firing `onInteractionComplete` (NA-319: used to seed a block
   * that is already answered on mount/rehydration, not a live submission —
   * `onInteractionComplete` is skipped so relaunch doesn't re-trigger
   * completion-tracking side effects for an interaction that already
   * happened in a prior session).
   */
  revealImmediately: () => void;
}

export function useSubmitRevealTransition({
  prefersReducedMotion,
  onInteractionComplete,
  submitTransitionMs,
  resultsEnterDelayMs,
  initialPhase = 'question',
}: UseSubmitRevealTransitionOptions): UseSubmitRevealTransitionResult {
  const [transitionPhase, setTransitionPhase] =
    useState<SubmitRevealTransitionPhase>(initialPhase);
  const completionTriggeredRef = useRef(initialPhase !== 'question');

  const triggerCompletion = useCallback(() => {
    if (completionTriggeredRef.current) {
      return;
    }

    completionTriggeredRef.current = true;
    onInteractionComplete?.();
  }, [onInteractionComplete]);

  const resetTransition = useCallback(() => {
    completionTriggeredRef.current = false;
    setTransitionPhase('question');
  }, []);

  const startTransition = useCallback(() => {
    if (transitionPhase !== 'question') {
      return;
    }

    if (prefersReducedMotion) {
      setTransitionPhase('results');
      triggerCompletion();
      return;
    }

    setTransitionPhase('submitting');
  }, [prefersReducedMotion, transitionPhase, triggerCompletion]);

  useEffect(() => {
    if (prefersReducedMotion || transitionPhase !== 'submitting') {
      return undefined;
    }

    const revealTimer = window.setTimeout(() => {
      setTransitionPhase('results-entering');
      triggerCompletion();
    }, submitTransitionMs);

    return () => window.clearTimeout(revealTimer);
  }, [prefersReducedMotion, submitTransitionMs, transitionPhase, triggerCompletion]);

  useEffect(() => {
    if (prefersReducedMotion || transitionPhase !== 'results-entering') {
      return undefined;
    }

    const settleTimer = window.setTimeout(() => {
      setTransitionPhase('results');
    }, resultsEnterDelayMs);

    return () => window.clearTimeout(settleTimer);
  }, [prefersReducedMotion, resultsEnterDelayMs, transitionPhase]);

  const revealImmediately = useCallback(() => {
    completionTriggeredRef.current = true;
    setTransitionPhase('results');
  }, []);

  return {
    transitionPhase,
    isQuestionStageVisible:
      transitionPhase === 'question' || transitionPhase === 'submitting',
    isResultsStageVisible:
      transitionPhase === 'results-entering' || transitionPhase === 'results',
    isSubmitting: transitionPhase === 'submitting',
    startTransition,
    resetTransition,
    revealImmediately,
  };
}
